import { checkBoxActions, textFieldActions } from "./SLActionManagerRequests";
import { ISLEntryGridItem } from "../../schemas/SLEntryGridSchemas";
import { SLEntriesError, RuntimeError } from "../../services/ErrorHelpers";
import { INotificationMessagesState } from "../NotificationMessages";
import { IProcessingStatus } from "../SLEntriesDetails";
import { slEntryGridItemsToEntryIds, slEntryGridItemsToEntryIdsAndBatchRefGuids } from "../SLEntriesGrid/utilities";
import { IUserProviderState } from "../../contexts/UserContext";

const entryWordSingularOrPlural = (c: number) => (c === 1 ? "Entry" : "Entries");

export interface IMessages {
  type: keyof INotificationMessagesState;
  messages: string[];
  processingStatus: IProcessingStatus | Record<string, unknown>;
}

export async function onProceedTextField(
  getTokenFn: IUserProviderState["accessToken"],
  name: "AddPosters" | "SendToPoster" | "NeedClarification",
  items: Array<Pick<ISLEntryGridItem, "slEntryId">>,
  userText: string
): Promise<IMessages> {
  const RequestMap = textFieldActions(getTokenFn);
  const results: any = {};

  try {
    const ids = slEntryGridItemsToEntryIds(items);
    const messages = await RequestMap[name](ids, userText);
    results.processingStatus = messages;
    if (messages.errors > 0) {
      const message = {
        AddPosters: `Unable to Add Posters to ${messages.errors} ${entryWordSingularOrPlural(messages.errors)}.`,
        NeedClarification: `Unable to Request Clarifications to ${messages.errors} ${entryWordSingularOrPlural(
          messages.errors
        )}.`,
        SendToPoster: `Unable to Sent to Posters to ${messages.errors} ${entryWordSingularOrPlural(messages.errors)}.`
      };
      results.type = "errors";
      results.messages = [message[name], "See Processing Status for more information."];
    } else {
      const message = {
        AddPosters: `Added Posters to ${messages.successes} ${entryWordSingularOrPlural(messages.successes)}.`,
        NeedClarification: `Clarifications have been requested for ${messages.successes} ${entryWordSingularOrPlural(
          messages.successes
        )}.`,
        SendToPoster: `Sent ${messages.successes} ${entryWordSingularOrPlural(messages.successes)} to Posters.`
      };
      results.type = "success";
      results.messages = [message[name]];
    }
  } catch (e) {
    if (e instanceof SLEntriesError) {
      results.type = "errors";
      results.messages = [`Error performing ${name}.`, ...e.messages];
    } else {
      results.type = "errors";
      results.messages = [`Error performing ${name}.`];
    }
  }
  return {
    type: results.type,
    messages: results.messages,
    processingStatus: results.processingStatus || {}
  };
}

export async function onProceedCheckbox(
  getTokenFn: IUserProviderState["accessToken"],
  name: "ApproveEntries" | "RecallEntries" | "Delete" | "Post" | "DeleteAttachments",
  items: Array<Pick<ISLEntryGridItem, "slEntryId" | "statusId" | "status" | "batchRefguid">>
): Promise<IMessages> {
  const RequestMap = checkBoxActions(getTokenFn);

  let type: IMessages["type"] = "errors";
  let messages: string[] = [];
  let processingStatus: IProcessingStatus;
  try {
    const ids = slEntryGridItemsToEntryIds(items);
    if (name === "RecallEntries") {
      processingStatus = await RequestMap[name](
        ids,
        items.some((gridItem) => gridItem.statusId === 10)
      );
    } else if (name === "Delete") {
      const { slEntryIds, slBatchRefGuids } = slEntryGridItemsToEntryIdsAndBatchRefGuids(items);
      processingStatus = await RequestMap[name](slEntryIds, slBatchRefGuids);
    } else {
      processingStatus = await RequestMap[name](ids);
    }
    if (!processingStatus) {
      throw new RuntimeError("No Statuses Retrieved from Action");
    }

    if (processingStatus.errors > 0) {
      const message = {
        Post: `Failed to Post ${processingStatus.errors} ${entryWordSingularOrPlural(processingStatus.errors)}.`,
        RecallEntries: `Failed to Recall ${processingStatus.errors} ${entryWordSingularOrPlural(
          processingStatus.errors
        )}.`,
        ApproveEntries: `Failed to Approve ${processingStatus.errors} ${entryWordSingularOrPlural(
          processingStatus.errors
        )}.`,
        Delete: `Failed to Delete ${processingStatus.errors} ${entryWordSingularOrPlural(processingStatus.errors)}.`,
        DeleteAttachments: `Failed to Delete Attachments.`
      };
      type = "errors";
      messages = [message[name], "See Processing Status for more information."];
    } else {
      const message = {
        Post: `Posted ${processingStatus.successes} ${entryWordSingularOrPlural(processingStatus.successes)}.`,
        RecallEntries: `Recalled ${processingStatus.successes} ${entryWordSingularOrPlural(
          processingStatus.successes
        )}.`,
        ApproveEntries: `Approved ${processingStatus.successes} ${entryWordSingularOrPlural(
          processingStatus.successes
        )} and submitted to SAP.`,
        Delete: `Deleted ${processingStatus.successes} ${entryWordSingularOrPlural(processingStatus.successes)}.`,
        DeleteAttachments: `Deleted Attachments.`
      };
      type = "success";
      messages = [message[name]];
    }
  } catch (e) {
    if (e instanceof SLEntriesError) {
      type = "errors";
      messages = [`Error performing ${name}.`, ...e.messages];
    } else {
      type = "errors";
      messages = [`Error performing ${name}.`];
    }
    processingStatus = {
      errors: 0,
      successes: 0,
      statuses: {}
    };
  }

  return {
    type,
    messages,
    processingStatus
  };
}
